/* FilterUsernamesRenderer.js */

"use strict";

const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;
const TextareaControl = uiCore.Control.Textfield.Textarea;

const MODAL_TITLE = "Syslog Inbound Username Filter";
const FILTER_MODE_ALLOW = "allow";
const FILTER_MODE_RESTRICT = "restrict";

/**
 * @class FilterUsernamesRenderer
 */
class FilterUsernamesRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = this._createRootElement();

        this._cancelButton = new ButtonControl({label: "Cancel", darkTheme: true});
        this._okButton = new ButtonControl({
            label: "OK",
            darkTheme: true,
            active: true,
        });

        this._filterModeRadio = new InputRadioControl({
            darkTheme: true,
            labelShown: false,
            options: [
                {
                    label: "Allow messages from listed usernames to trigger events",
                    value: FILTER_MODE_ALLOW,
                },
                {
                    label: "Restrict messages from listed usernames to trigger events",
                    value: FILTER_MODE_RESTRICT,
                },
            ],
            value: this._model._filterMode ? FILTER_MODE_ALLOW : FILTER_MODE_RESTRICT,
        });

        this._usernamesInput = new TextareaControl({
            darkTheme: true,
            placeholder: "Enter regular expressions separated by new lines",
            value: this._model._usernames.join("\n"),
        });

        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("error");

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    getFilterMode() {
        return this._filterModeRadio.value === FILTER_MODE_ALLOW;
    }

    getUsernames() {
        const value = this._usernamesInput.value || "";
        return value.split("\n").filter((line) => line.trim() !== "");
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._cancelButton.on("UserClick", (this._onCancelClick = () => this._model.closePopUp()));
        this._okButton.on("UserClick", (this._onOkClick = () => this._save()));
        this._usernamesInput.on("UserValue", (this._onUsernamesChange = () => this._setError("")));
    }

    _unregisterListeners() {
        this._cancelButton.removeListener("UserClick", this._onCancelClick);
        this._okButton.removeListener("UserClick", this._onOkClick);
        this._usernamesInput.removeListener("UserValue", this._onUsernamesChange);
    }

    _render() {
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();

        const title = document.createElement("h2");
        title.innerText = MODAL_TITLE;
        root.appendChild(title);

        root.appendChild(this._filterModeRadio.renderer.rootElement);

        const usernamesLabel = document.createElement("label");
        usernamesLabel.innerText = "Usernames";
        root.appendChild(usernamesLabel);
        root.appendChild(this._usernamesInput.renderer.rootElement);
        root.appendChild(this._errorContainer);

        const buttonContainer = document.createElement("div");
        buttonContainer.classList.add("button-container");
        buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
        buttonContainer.appendChild(this._okButton.renderer.rootElement);
        root.appendChild(buttonContainer);

        return new Promise((resolve) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _save() {
        const usernames = this.getUsernames();
        const invalidRegex = this._validateRegexPatterns(usernames);
        if (invalidRegex) {
            this._setError(`Invalid regular expression: ${invalidRegex}`);
            return;
        }
        this._setError("");
        this._model._syslog.userIncludeList = this.getFilterMode() ? usernames : null;
        this._model._syslog.userExcludeList = this.getFilterMode() ? null : usernames;
        return this._model.save();
    }

    _validateRegexPatterns(patterns) {
        for (const pattern of patterns) {
            try {
                new RegExp(pattern);
            } catch (e) {
                return pattern;
            }
        }
        return null;
    }

    _setError(msg) {
        this._errorContainer.innerText = msg;
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this._cancelButton.shutdown(),
            this._okButton.shutdown(),
            this._filterModeRadio.shutdown(),
            this._usernamesInput.shutdown(),
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._okButton = null;
                this._cancelButton = null;
                this._filterModeRadio = null;
                this._usernamesInput = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("filter-usernames", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    /********************************************/
    /* END PRIVATE RENDERER METHODS             */
    /********************************************/
}

module.exports = FilterUsernamesRenderer;
